import { Button, ExclusiveChoice, Help, ModalPanel, Title } from "dash-styled"
import React, { FC, ReactNode, useCallback, useState } from "react"
import {
    APIOrgMemberRole,
    apiOrgMemberRoleList
} from "../../../../../../Models/API/APIOrgMember"
import { DelimitedTextInput } from "../../../../../Common/DelimitedTextInput/DelimitedTextInput"
import { Labeled } from "../../../../../Common/Render/Common/Labeled/Labeled"
import capitalize from "lodash/capitalize"

interface OrgMemberAddModalProps {
    isSaving: boolean
    onClose: () => void
    onSubmit: (emails: string[], role: APIOrgMemberRole) => void
}

function renderRole(role: APIOrgMemberRole): ReactNode {
    return (
        <>
            {capitalize(role)}
            <Help
                className="ml-1 align-text-bottom"
                tooltipPlacement="right"
                text={
                    role === "MEMBER"
                        ? "Member can be invited to projects as a worker"
                        : "Owner can manage collaborators, workers and projects in organization"
                }
            />
        </>
    )
}

export const OrgMemberAddModal: FC<OrgMemberAddModalProps> = ({
    isSaving,
    onClose,
    onSubmit
}) => {
    const [emails, setEmails] = useState<string[] | undefined>(undefined)
    const [role, setRole] = useState<APIOrgMemberRole | undefined>(undefined)
    const handleSubmit = useCallback(() => {
        if (emails !== undefined && role !== undefined) {
            onSubmit(emails, role)
        }
    }, [emails, onSubmit, role])

    return (
        <ModalPanel onClose={onClose} className="max-w-2xl w-full">
            <Title variant="heading2" weight="semibold" component="h2">
                Add new member
            </Title>
            <DelimitedTextInput
                className="mb-1"
                label="Email address"
                variant="bordered"
                newlineExpanding={true}
                placeholder="e.g. your_email@company.com, another_email@company.com"
                value={emails}
                onChange={setEmails}
            />
            <Labeled label="Role" errors={[]}>
                <ExclusiveChoice
                    withPanel={false}
                    choices={[...apiOrgMemberRoleList]}
                    selectedIndex={
                        role === undefined
                            ? -1
                            : apiOrgMemberRoleList.indexOf(role)
                    }
                    renderChoice={renderRole}
                    onClick={index => setRole(apiOrgMemberRoleList[index])}
                />
            </Labeled>
            <div className="flex justify-center">
                <Button color="plain" onClick={onClose} className="w-48 mr-4">
                    Cancel
                </Button>
                <Button
                    disabled={
                        isSaving ||
                        emails === undefined ||
                        emails.length === 0 ||
                        role === undefined
                    }
                    className="w-48"
                    onClick={handleSubmit}
                >
                    {isSaving ? "Sending..." : "Send invitation"}
                </Button>
            </div>
        </ModalPanel>
    )
}
